import { useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { TypeCStandardCheckProgress } from '../../components/TypeCStandardCheckProgress'
import { MachineCharacteristicsExtractionProgress } from '../../components/MachineCharacteristicsExtractionProgress'
import { RerunCandidateReview } from './RerunCandidateReview'
import { RerunTypeBQuestionnairePanel } from './RerunTypeBQuestionnairePanel'
import { useRerunStandardApplicabilitySheet } from './useRerunStandardApplicabilitySheet'

export type RerunStandardApplicabilitySheetProps = {
  complianceId: string
  isOpen: boolean
  onClose: () => void
}

export function RerunStandardApplicabilitySheet({
  complianceId,
  isOpen,
  onClose,
}: RerunStandardApplicabilitySheetProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const sheet = useRerunStandardApplicabilitySheet(complianceId)
  const typeCStartedRef = useRef(false)
  const typeBExtractStartedRef = useRef(false)
  const runTypeCProgressRef = useRef(sheet.runTypeCProgress)
  const runTypeBExtractProgressRef = useRef(sheet.runTypeBExtractProgress)
  const resetRef = useRef(sheet.reset)
  runTypeCProgressRef.current = sheet.runTypeCProgress
  runTypeBExtractProgressRef.current = sheet.runTypeBExtractProgress
  resetRef.current = sheet.reset

  useEffect(() => {
    if (!isOpen) {
      typeCStartedRef.current = false
      typeBExtractStartedRef.current = false
      resetRef.current()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || sheet.step !== 'typeCProgress' || typeCStartedRef.current) {
      return
    }
    typeCStartedRef.current = true
    void runTypeCProgressRef.current()
  }, [isOpen, sheet.step])

  useEffect(() => {
    if (
      !isOpen ||
      sheet.step !== 'typeBExtractProgress' ||
      typeBExtractStartedRef.current
    ) {
      return
    }
    typeBExtractStartedRef.current = true
    void runTypeBExtractProgressRef.current()
  }, [isOpen, sheet.step])

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return
    }
    const canClose = sheet.requestLeave()
    if (canClose) {
      onClose()
    }
  }

  const handleConfirmLeave = async () => {
    await sheet.confirmLeave()
    onClose()
  }

  const handleAccept = async () => {
    try {
      await sheet.acceptCommit()
      sheet.reset()
      onClose()
    } catch {
      // acceptCommit sets sheet.error before rethrowing; keep sheet open on failure.
    }
  }

  const scopeConfirmDisabled = !sheet.scopeTypeC && !sheet.scopeTypeB

  const finalReviewItems = [
    ...(sheet.draft?.typeC?.selectedReferences != null
      ? [...sheet.draft.typeC.selectedReferences].map((reference) => ({
          reference,
          scope: 'typeC' as const,
        }))
      : []),
    ...(sheet.draft?.typeB?.selectedReferences != null
      ? [...sheet.draft.typeB.selectedReferences].map((reference) => ({
          reference,
          scope: 'typeB' as const,
        }))
      : []),
  ]

  return (
    <>
      <Sheet modal={false} open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side='right'
          showOverlay={false}
          className='flex w-full flex-col gap-0 sm:max-w-2xl'
        >
          <SheetHeader className='shrink-0 border-b px-4 py-3'>
            <SheetTitle>{t('standardsRerun.sheetTitle')}</SheetTitle>
          </SheetHeader>

          <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-4 py-4'>
            {sheet.error != null && (
              <Alert variant='destructive'>
                <AlertDescription>{sheet.error}</AlertDescription>
              </Alert>
            )}

            {sheet.step === 'scope' && (
              <div className='space-y-4'>
                <Alert variant='destructive'>
                  <AlertDescription className='whitespace-pre-line'>
                    {t('standardsRerun.scope.confirmBody')}
                  </AlertDescription>
                </Alert>
                <p className='text-sm font-medium text-foreground'>
                  {t('standardsRerun.scope.selectTypeLabel')}
                </p>
                <div className='space-y-3'>
                  <label className='flex items-start gap-3'>
                    <Checkbox
                      checked={sheet.scopeTypeC}
                      onCheckedChange={(checked) =>
                        sheet.setScopeTypeC(checked === true)
                      }
                    />
                    <div>
                      <Label>{t('standardsRerun.scope.typeCLabel')}</Label>
                      <p className='text-xs text-muted-foreground'>
                        {t('standardsRerun.scope.typeCHelper')}
                      </p>
                    </div>
                  </label>
                  <label className='flex items-start gap-3'>
                    <Checkbox
                      checked={sheet.scopeTypeB}
                      onCheckedChange={(checked) =>
                        sheet.setScopeTypeB(checked === true)
                      }
                    />
                    <div>
                      <Label>{t('standardsRerun.scope.typeBLabel')}</Label>
                      <p className='text-xs text-muted-foreground'>
                        {t('standardsRerun.scope.typeBHelper')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {sheet.step === 'typeCProgress' && (
              <TypeCStandardCheckProgress state={sheet.typeCProgressState} />
            )}

            {sheet.step === 'typeCReview' && sheet.draft?.typeC != null && (
              <RerunCandidateReview
                candidates={sheet.draft.typeC.candidates}
                selectedReferences={sheet.draft.typeC.selectedReferences}
                onSelectedReferencesChange={sheet.updateTypeCSelection}
                onContinue={sheet.continueFromTypeCReview}
                continueLabel={
                  sheet.draft.runTypeB
                    ? t('standardsRerun.continueToTypeB')
                    : t('standardsRerun.continueToFinalReview')
                }
                emptyMessage={t('standardsRerun.typeCReview.empty')}
                isBusy={sheet.isTypeCRunning}
              />
            )}

            {sheet.step === 'typeBExtractProgress' && (
              <MachineCharacteristicsExtractionProgress
                state={sheet.typeBExtractProgressState}
              />
            )}

            {sheet.step === 'typeBQuestionnaire' && sheet.draft?.typeB != null && (
              <RerunTypeBQuestionnairePanel
                complianceId={complianceId}
                answersJson={sheet.draft.typeB.answersJson}
                evidenceJson={sheet.draft.typeB.evidenceJson}
                extractionSkipped={sheet.draft.typeB.extractionSkipped}
                onAnswersChange={sheet.updateTypeBAnswers}
                onGenerate={(answers) =>
                  void sheet.generateTypeBPreview(answers)
                }
                isGenerating={sheet.isTypeBPreviewRunning}
              />
            )}

            {sheet.step === 'typeBReview' && sheet.draft?.typeB != null && (
              <RerunCandidateReview
                candidates={sheet.draft.typeB.candidates}
                selectedReferences={sheet.draft.typeB.selectedReferences}
                onSelectedReferencesChange={sheet.updateTypeBSelection}
                onContinue={sheet.continueFromTypeBReview}
                continueLabel={t('standardsRerun.continueToFinalReview')}
                emptyMessage={t('standardsRerun.typeBReview.empty')}
                isBusy={sheet.isTypeBPreviewRunning}
              />
            )}

            {sheet.step === 'finalReview' && (
              <div className='space-y-4'>
                <p className='text-sm text-muted-foreground'>
                  {t('standardsRerun.finalReview.intro')}
                </p>
                {finalReviewItems.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>
                    {t('standardsRerun.finalReview.empty')}
                  </p>
                ) : (
                  <ul className='max-h-64 space-y-2 overflow-y-auto text-sm'>
                    {finalReviewItems.map((item) => (
                      <li
                        key={`${item.scope}-${item.reference}`}
                        className='rounded-md border px-3 py-2'
                      >
                        <span className='font-medium'>{item.reference}</span>
                        <span className='ml-2 text-xs text-muted-foreground'>
                          ({item.scope === 'typeC' ? 'Type C' : 'Type B'})
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <SheetFooter className='shrink-0 flex-row justify-end gap-2 border-t px-4 py-3'>
            {sheet.step === 'scope' && (
              <Button
                type='button'
                onClick={sheet.confirmScope}
                disabled={scopeConfirmDisabled}
              >
                {t('standardsRerun.scope.confirmButton')}
              </Button>
            )}
            {sheet.step === 'finalReview' && (
              <Button
                type='button'
                onClick={() => void handleAccept()}
                disabled={sheet.isCommitting}
              >
                {sheet.isCommitting && (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                )}
                {t('standardsRerun.finalReview.acceptButton')}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={sheet.leaveConfirmOpen}
        onOpenChange={sheet.setLeaveConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('standardsRerun.leaveConfirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('standardsRerun.leaveConfirm.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('standardsRerun.leaveConfirm.stay')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleConfirmLeave()}>
              {t('standardsRerun.leaveConfirm.leave')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
